import {
  NodeListReq,
  ConfoundingNodesReq,
  OutcomeNodesDef,
  TreatmentNodesDef,
  ConfoundingNodesDef,
} from "./type/node-def";
import {
  CateReq,
  CateDef,
  ListUserModelsDef,
  RenameModelReq,
  CatePredictReq,
  CatePredictDef,
  ConditionalEffectsDef,
  TreatmentHistogramReq,
  TreatmentHistogramDef,
  TreatmentSplitSummaryReq,
  TreatmentSplitSummaryDef
} from "./type/cate-def";

import { OutcomeNodesAdapter } from "./adapter/outcome-nodes-def";
import { TreatmentNodesAdapter } from "./adapter/treatment-nodes-def";
import { ConfoundingNodesAdapter } from "./adapter/confounders-def";
import {
  CateAdapter,
  ListUserModelsAdapter,
  CatePredictAdapter,
  ConditionalEffectsAdapter,
  RenameModelAdapter
} from "./adapter/cate-def";

import { ICateRepo } from "./irepo";

import { TError } from "../../types/error";

export class CateUsecase {
  private repo: ICateRepo;
  private error: (error: TError) => void;

  constructor(repo: ICateRepo, error: (error: TError) => void) {
    this.repo = repo;
    this.error = error;
  }

  async fetchOutcomeNodes(req: NodeListReq): Promise<OutcomeNodesDef> {
    try {
      const dto = OutcomeNodesAdapter.toInputDTO(req);
      const data = await this.repo.fetchOutcomeNodes(dto);
      return OutcomeNodesAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchTreatmentNodes(req: NodeListReq): Promise<TreatmentNodesDef> {
    try {
      const dto = TreatmentNodesAdapter.toInputDTO(req);
      const data = await this.repo.fetchTreatmentNodes(dto);
      return TreatmentNodesAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchConfoundingNodes(req: ConfoundingNodesReq): Promise<ConfoundingNodesDef> {
    try {
      const dto = ConfoundingNodesAdapter.toInputDTO(req);
      const data = await this.repo.fetchConfoundingNodes(dto);
      return ConfoundingNodesAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }


  async fetchTreatmentHistogram(
    req: TreatmentHistogramReq
  ): Promise<TreatmentHistogramDef> {
    try {
      const dto: any = { asset_id: req.asset_id, target_node: req.target_node };
      if (req.sigma_bin_width != null) {
        dto.sigma_bin_width = req.sigma_bin_width;
      }
      const data = await this.repo.fetchTreatmentHistogram(dto);
      return data as TreatmentHistogramDef;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async fetchTreatmentSplitSummary(
    req: TreatmentSplitSummaryReq
  ): Promise<TreatmentSplitSummaryDef> {
    try {
      const dto = {
        asset_id: req.asset_id,
        target_node: req.target_node,
        split_value: String(req.split_value),
      };
      const data = await this.repo.fetchTreatmentSplitSummary(dto);
      return data as TreatmentSplitSummaryDef;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  // median 未提供時のフォールバック: prob_density(y) の累積が半分に達する x（中央値・整数丸め）
  // ヒストグラムの累積が全体の半分に達する x（＝中央値）を線形補間で求める。
  // level0（未満）と level1（以上）を可能な限り半々にするため整数丸めはしない。
  computeHistogramMedian(hist?: {
    x: number[];
    prob_density: number[];
  }): number | null {
    if (!hist || !hist.x?.length) return null;
    const x = hist.x;
    const y = hist.prob_density;
    const total = y.reduce((s, v) => s + (v ?? 0), 0);
    if (total <= 0) return null;
    const half = total / 2;
    let acc = 0;
    for (let i = 0; i < x.length; i++) {
      const prev = acc;
      acc += y[i] ?? 0;
      if (acc >= half) {
        // bin i 内で累積が half に達する位置を、隣接 x 間で線形補間
        const xPrev = i > 0 ? x[i - 1] : x[i] - (x[1] - x[0] || 1);
        const freq = y[i] ?? 0;
        const frac = freq > 0 ? (half - prev) / freq : 0.5;
        const value = xPrev + frac * (x[i] - xPrev);
        return parseFloat(value.toFixed(2));
      }
    }
    return parseFloat(x[x.length - 1].toFixed(2));
  }

  async calculateCate(req: CateReq): Promise<CateDef> {
    try {
      const dto = CateAdapter.toInputDTO(req);
      const data = await this.repo.calculateCate(dto);
      return CateAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  // POST のみ（task_id を返す）。UI で task_id を永続化しポーリングする用。
  async postCalculateCate(req: CateReq): Promise<{ task_id: string }> {
    try {
      const dto = CateAdapter.toInputDTO(req);
      return await this.repo.postCalculateCate(dto);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  // task_result（CATE結果）を CateDef に整形。
  formatCateResult(data: any): CateDef {
    return CateAdapter.toOutputDTO(data);
  }

  async predictEffect(model_id: string, req: CatePredictReq): Promise<CatePredictDef> {
    try {
      const dto = CatePredictAdapter.toInputDTO(req);
      const data = await this.repo.predictEffect(model_id, dto);
      return CatePredictAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async getConditionalEffects(model_id: string, cate?: number): Promise<ConditionalEffectsDef> {
    try {
      const data = await this.repo.getConditionalEffects(model_id, cate);
      return ConditionalEffectsAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async listUserModels(): Promise<ListUserModelsDef> {
    try {
      const data = await this.repo.listUserModels();
      return ListUserModelsAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async renameModel(model_id: string, req: RenameModelReq): Promise<string> {
    try {
      const dto = RenameModelAdapter.toInputDTO(req);
      const data = await this.repo.renameModel(model_id, dto);
      return RenameModelAdapter.toOutputDTO(data);
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }

  async deleteModel(model_id: string): Promise<string> {
    try {
      const data = await this.repo.deleteModel(model_id);
      return data as string;
    } catch (error) {
      this.error({ id: null, error });
      throw error;
    }
  }
}